import { RunningSchedule, ScheduleFormData } from '../types/schedule';
import { getApiBaseUrl, getApiTimeout } from '../config/api';

// API 요청/응답 타입 정의
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface RunningScheduleRequest {
  title: string;           // 제목 (필수, 1-100자)
  date: string;            // 날짜 (필수, YYYYMMDD 형식)
  startTime: string;       // 시작시간 (필수, HHMM 형식)
  endTime: string;         // 종료시간 (필수, HHMM 형식)
  x: number;              // X 좌표 (필수)
  y: number;              // Y 좌표 (필수)
  placeName: string;      // 장소명 (필수, 1-100자)
  placeDetail?: string;   // 상세장소 (선택, 0-50자)
  placeUrl?: string;      // 장소 URL (선택)
  addressName?: string;   // 주소 (선택)
}

interface RunningScheduleResponse {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  x: number;
  y: number;
  placeName: string;
  placeDetail?: string;
  placeUrl?: string;
  addressName?: string;
  weatherInfo?: any;
  createdAt?: string;
}

// HTTP 클라이언트 클래스
class ApiClient {
  private baseURL: string;
  private timeout: number;

  constructor() {
    this.baseURL = getApiBaseUrl();
    this.timeout = getApiTimeout();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      signal: AbortSignal.timeout(this.timeout),
    };

    try {
      console.log(`API 호출: ${config.method || 'GET'} ${url}`);
      console.log('요청 헤더:', config.headers);
      if (config.body) {
        console.log('요청 본문:', config.body);
      }
      
      const response = await fetch(url, config);
      
      console.log('응답 상태:', response.status, response.statusText);
      console.log('응답 헤더:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('에러 응답 본문:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      console.log('API 응답:', data);
      
      return data;
    } catch (error) {
      console.error('API 호출 오류:', error);
      throw error;
    }
  }

  // GET 요청
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST 요청
  async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT 요청
  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // DELETE 요청
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// API 클라이언트 인스턴스
const apiClient = new ApiClient();

// 날짜 형식 변환: YYYY-MM-DD -> YYYYMMDD
const formatDateForApi = (date: string): string => {
  return date.replace(/-/g, '');
};

// 시간 형식 변환: HH:mm -> HHMM
const formatTimeForApi = (time: string): string => {
  return time.replace(':', '');
};

// 스케줄 관련 API 함수들
export const scheduleApi = {

  // 러닝 스케줄 생성
  async createRunningSchedule(scheduleData: {
    title: string;
    date: string;
    startTime: string;
    endTime: string;
    x: number;
    y: number;
    placeName: string;
    placeDetail?: string;
    placeUrl?: string;
    addressName?: string;
  }): Promise<RunningScheduleResponse> {
    try {
      const requestData: RunningScheduleRequest = {
        title: scheduleData.title,
        date: formatDateForApi(scheduleData.date),
        startTime: formatTimeForApi(scheduleData.startTime),
        endTime: formatTimeForApi(scheduleData.endTime),
        x: scheduleData.x,
        y: scheduleData.y,
        placeName: scheduleData.placeName,
        placeDetail: scheduleData.placeDetail,
        placeUrl: scheduleData.placeUrl,
        addressName: scheduleData.addressName,
      };

      const response = await apiClient.post<{ success: boolean; data: RunningScheduleResponse; message: string }>('/api/schedules/running', requestData);
      
      // success 체크
      if (!response.success) {
        console.error('스케줄 생성 실패:', response.message);
        throw new Error(response.message);
      }
      
      return response.data.data;
    } catch (error) {
      console.error('러닝 스케줄 생성 오류:', error);
      throw error;
    }
  },

  // 러닝 스케줄 조회 (날짜 범위로 조회) - 기본 스케줄 데이터 반환
  async getRunningSchedulesByDateRange(startDate: string, endDate: string): Promise<RunningScheduleResponse[]> {
    try {
      // 날짜 형식을 YYYYMMDD로 변환
      const startDateFormatted = startDate.replace(/-/g, '');
      const endDateFormatted = endDate.replace(/-/g, '');
      
      const response = await apiClient.get<{ success: boolean; data: RunningScheduleResponse[] | null; message: string }>(
        `/api/schedules/running/range?startDate=${startDateFormatted}&endDate=${endDateFormatted}`
      );
      console.log('API 응답 전체 (날짜 범위):', response);
      
      // success 체크
      if (!response.success) {
        console.error('API 요청 실패:', response.message);
        throw new Error(response.message);
      }
      
      // data가 배열인지 확인
      if (Array.isArray(response.data)) {
        return response.data;
      } else {
        console.warn('데이터가 배열이 아닙니다:', response.data);
        return [];
      }
    } catch (error) {
      console.error('러닝 스케줄 조회 오류 (날짜 범위):', error);
      throw error;
    }
  },

  // 러닝 스케줄 상세 조회 (ID로 조회) - 기상정보 포함
  async getRunningScheduleById(id: string): Promise<RunningScheduleResponse> {
    try {
      const response = await apiClient.get<{ success: boolean; data: RunningScheduleResponse | null; message: string }>(
        `/api/schedules/running/${id}`
      );
      console.log(`스케줄 상세 조회 API 응답 (ID: ${id}):`, response);
      
      // success 체크
      if (!response.success) {
        console.error('API 요청 실패:', response.message);
        throw new Error(response.message);
      }
      
      // data가 존재하는지 확인
      if (response.data !== null) {
        return response.data as unknown as RunningScheduleResponse;
      } else {
        console.warn('데이터가 없습니다:', response.data);
        throw new Error('스케줄 데이터를 찾을 수 없습니다.');
      }
    } catch (error) {
      console.error(`러닝 스케줄 상세 조회 오류 (ID: ${id}):`, error);
      throw error;
    }
  },

  // 러닝 스케줄 삭제
  async deleteRunningSchedule(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        `/api/schedules/running/${id}`
      );
      console.log(`스케줄 삭제 API 응답 (ID: ${id}):`, response);
      
      return {
        success: response.success,
        message: response.message || '삭제 완료'
      };
    } catch (error) {
      console.error(`러닝 스케줄 삭제 오류 (ID: ${id}):`, error);
      throw error;
    }
  },

  // 모든 스케줄 조회 (로컬스토리지에서)
  async getAllSchedules(): Promise<RunningSchedule[]> {
    return this.getSchedulesFromLocalStorage();
  },

  // API 응답을 RunningSchedule 타입으로 변환
  mapApiResponseToSchedule(apiResponse: RunningScheduleResponse): RunningSchedule {
    // API 날짜 형식 (YYYYMMDD)을 프론트엔드 형식 (YYYY-MM-DD)으로 변환
    const formatDateFromApi = (dateStr: string): string => {
      if (dateStr.length === 8) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    };

    return {
      id: apiResponse.id,
      title: apiResponse.title,
      location: apiResponse.placeName,
      startTime: apiResponse.startTime,
      endTime: apiResponse.endTime,
      date: formatDateFromApi(apiResponse.date),
      createdAt: apiResponse.createdAt || new Date().toISOString(),
      weatherInfo: apiResponse.weatherInfo, // weatherInfo 포함
      placeDetail: apiResponse.placeDetail,
      addressName: apiResponse.addressName,
      placeUrl: apiResponse.placeUrl,
    };
  },

  // 로컬스토리지 관련 함수들 (API 실패 시 fallback)
  getSchedulesFromLocalStorage(): RunningSchedule[] {
    const savedSchedules = localStorage.getItem('runningSchedules');
    return savedSchedules ? JSON.parse(savedSchedules) : [];
  },

  saveScheduleToLocalStorage(schedule: RunningSchedule): void {
    const schedules = this.getSchedulesFromLocalStorage();
    schedules.push(schedule);
    localStorage.setItem('runningSchedules', JSON.stringify(schedules));
  },

  createScheduleInLocalStorage(scheduleData: ScheduleFormData & { date: string; coordinates?: { x: string; y: string } }): RunningSchedule {
    const newSchedule: RunningSchedule = {
      id: Date.now().toString(),
      title: scheduleData.title,
      location: scheduleData.location,
      startTime: scheduleData.startTime,
      endTime: scheduleData.endTime,
      date: scheduleData.date,
      createdAt: new Date().toISOString(),
    };

    this.saveScheduleToLocalStorage(newSchedule);
    return newSchedule;
  },

  updateScheduleInLocalStorage(updatedSchedule: RunningSchedule): void {
    const schedules = this.getSchedulesFromLocalStorage();
    const index = schedules.findIndex(s => s.id === updatedSchedule.id);
    if (index !== -1) {
      schedules[index] = updatedSchedule;
      localStorage.setItem('runningSchedules', JSON.stringify(schedules));
    }
  },

  deleteScheduleFromLocalStorage(id: string): void {
    const schedules = this.getSchedulesFromLocalStorage();
    const filteredSchedules = schedules.filter(s => s.id !== id);
    localStorage.setItem('runningSchedules', JSON.stringify(filteredSchedules));
  },
};
